import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { isFallbackAsset } from '@service/bugo/utils/courtesyContentGenerator';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import { Icon } from '@shared/components/icons';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useAsync } from 'react-use';

import {
  courtesyAssetAtom,
  courtesyContentStringAtom,
  courtesyEditRequestAtom,
  courtesyTextFontDeltaAtom,
  courtesyTextOffsetYAtom,
} from './courtesy.atom';

interface Props {
  openId: string;
}

//* 미리보기에서 조절 가능한 범위 (px)
const FONT_DELTA_MIN = -4;
const FONT_DELTA_MAX = 12;
const FONT_DELTA_STEP = 0.5;
const OFFSET_Y_MIN = -120;
const OFFSET_Y_MAX = 200;
const OFFSET_Y_STEP = 8;

//* 본문 줄간격은 body(text-15)의 line-height(1.375rem)를 상속받아 22px로 고정돼 있다.
//* 글씨 크기만 키우면 줄간격이 안 따라와 줄이 겹치므로 같은 비율로 함께 키운다.
const INHERITED_LINE_HEIGHT_PX = 22;

//* 화면 표시만 90%로 축소해 작은 폰에서도 이미지·조절 바·버튼이 한 화면에 들어오게 한다.
//* 저장·공유 캡처는 원래(100%) 크기 그대로 — 캡처 직전에 transform을 잠시 해제한다.
const PREVIEW_DISPLAY_SCALE = 0.9;

const adjustButtonClassName =
  'button min-h-[36px] shrink-0 rounded-md border bg-white px-2 text-13 font-bold text-gray-700';

const actionButtonClassName =
  'button min-h-[40px] gap-1 rounded-md border px-2 text-13 font-bold';

const saveImageToLocal = async (imageUrl: string) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  const file = new File([blob], 'image.jpeg', { type: 'image/jpeg' });
  const dataURL = URL.createObjectURL(file);

  return dataURL;
};

export const MemberCourtesyPreviewModal: FC<Props> = function MemberCourtesyPreviewModal({
  openId,
}: Props) {
  const { Download, OtherApps } = Icon();
  const courtesyAsset = useRecoilValue(courtesyAssetAtom);
  const imageUrl = courtesyAsset?.backgroundImage.url;
  const courtesyContentString = useRecoilValue(courtesyContentStringAtom);
  const divRef = useRef<HTMLDivElement | null>(null);
  //* 모달이 열릴 때만 자식이 렌더되므로 callback ref로 캡처 요소를 잡는다
  const [captureEl, setCaptureEl] = useState<HTMLDivElement | null>(null);
  //* 인라인 함수면 렌더마다 ref가 떼었다 붙으며(null→node) setState → 무한 재렌더 루프.
  //* 반드시 참조 고정(실사고: 조절 버튼 먹통 + 커서 깜빡임).
  const setCaptureRef = useCallback((node: HTMLDivElement | null) => {
    divRef.current = node;
    setCaptureEl(node);
  }, []);
  const scaleWrapRef = useRef<HTMLDivElement>(null);
  const [captureHeight, setCaptureHeight] = useState(0);
  const [localImageUrl, setLocalImageUrl] = useState<string>();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const isFallback = courtesyAsset ? isFallbackAsset(courtesyAsset._id) : false;

  //* 이미지를 보면서 본문 글씨 크기·세로 위치를 직접 맞춘다 (html2canvas 결과에도 그대로 반영됨)
  const [fontDelta, setFontDelta] = useRecoilState(courtesyTextFontDeltaAtom);
  const [offsetY, setOffsetY] = useRecoilState(courtesyTextOffsetYAtom);
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));
  const setEditRequest = useSetRecoilState(courtesyEditRequestAtom);

  const assetId = courtesyAsset?._id;

  //* 이미지를 바꾸면 기준 위치·크기가 달라지므로 조절값을 초기화한다.
  useEffect(() => {
    setFontDelta(0);
    setOffsetY(0);
  }, [assetId, setFontDelta, setOffsetY]);

  const baseFontSize = courtesyAsset?.textBox?.y1 ?? 12;
  const baseTop = courtesyAsset?.textBox?.y0 ?? 30;
  const fontSize = Math.max(baseFontSize + fontDelta, 8);
  // 기본 상태(fontDelta 0)에선 기존과 똑같은 22px가 나오도록 비율을 유지한다.
  const lineHeight = (INHERITED_LINE_HEIGHT_PX / Math.max(baseFontSize, 1)) * fontSize;
  const top = baseTop + offsetY;
  const isAdjusted = fontDelta !== 0 || offsetY !== 0;

  useEffect(() => {
    if (!captureEl) return;
    const update = () => setCaptureHeight(captureEl.offsetHeight);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(captureEl);
    return () => observer.disconnect();
  }, [captureEl]);

  //* 글씨를 키우거나 내리면 이미지 밖으로 넘칠 수 있고, 넘친 부분은 저장·공유 이미지에서 잘린다.
  const textRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [overflowed, setOverflowed] = useState(false);

  useEffect(() => {
    const container = divRef.current;
    const text = textRef.current;
    if (!container || !text) return;
    setOverflowed(text.offsetTop + text.offsetHeight > container.offsetHeight);
    //* captureHeight: 화살표로 이미지를 바꾸면 높이가 달라지므로 재평가 필요
  }, [fontSize, top, courtesyContentString, imageLoaded, captureHeight]);

  //* 모달을 닫고(Modal 내부 close와 동일한 방식) 본문 편집란으로 이동시킨다.
  const handleGoToEditClick = () => {
    setOpen(false);
    history.back();
    setTimeout(() => {
      // 이전 모달의 scroll lock 잔여 설정 초기화
      document.body.style.overflow = '';
      setEditRequest((prev) => prev + 1);
    }, 300);
  };

  useAsync(async () => {
    if (!imageUrl) return;

    if (isFallback) {
      setLocalImageUrl(imageUrl);
      return;
    }

    const corsImageModified = new Image();
    corsImageModified.crossOrigin = 'Anonymous';
    const s3Url = fileBucketUrl(`${imageUrl}`, 'raw');

    corsImageModified.src = s3Url + '?not-from-cache-please';

    const localUrl = await saveImageToLocal(corsImageModified.src);

    setLocalImageUrl(localUrl);
  }, [imageUrl, isFallback]);

  //* 표시용 축소(transform)를 잠시 해제하고 원래(100%) 크기로 캡처한다
  async function captureAtFullScale(el: HTMLElement) {
    const wrapper = scaleWrapRef.current;
    const prevTransform = wrapper ? wrapper.style.transform : '';
    const prevMargin = wrapper ? wrapper.style.marginBottom : '';
    if (wrapper) {
      wrapper.style.transform = 'none';
      wrapper.style.marginBottom = '0px';
    }
    try {
      //* 본문 글꼴(NotoSerifKR, font-display: swap)이 아직 안 내려왔으면 대체 글꼴로 캡처되므로 로딩 완료를 기다린다
      await document.fonts?.ready;
      //* html2canvas(≈46KB gz)는 이미지 저장 버튼을 누른 사람만 쓰므로 여기서 받는다 — 조문객 기본 번들에서 제외
      const { default: html2canvas } = await import('html2canvas');
      return await html2canvas(el, { useCORS: true, allowTaint: true });
    } finally {
      if (wrapper) {
        wrapper.style.transform = prevTransform;
        wrapper.style.marginBottom = prevMargin;
      }
    }
  }

  async function handleDownloadClick() {
    const div_ = divRef.current;
    if (!div_) return;
    const canvas = await captureAtFullScale(div_);
    const dataUrl = canvas.toDataURL('image/jpeg');
    const link = document.createElement('a');
    link.download = '답례인사.jpg';
    link.href = dataUrl;
    link.click();
  }

  async function handleShareClick() {
    const div_ = divRef.current;
    if (div_) {
      const canvas = await captureAtFullScale(div_);
      canvas.toBlob((blob) => {
        if (blob) {
          if (navigator.share) {
            navigator
              .share({
                files: [new File([blob], '답례인사.jpg', { type: 'image/jpeg' })],
                title: '답례인사',
                text: '답례인사를 공유합니다.',
              })
              .then(() => notificateSuccess(notificationInstance, '공유 성공!'))
              .catch(() => alert('공유에 실패했습니다.'));
          } else {
            alert(
              '이 브라우저는 공유기능을 지원하지 않습니다. 다운로드 받으셔서 직접 공유해주세요',
            );
          }
        }
      }, 'image/jpeg');
    }
  }

  return (
    <Modal
      openId={openId}
      position="start"
      frameClassName="!pt-2 !pb-2"
      className="!mb-1 !px-3 !pt-8 !pb-2"
    >
      <div
        ref={scaleWrapRef}
        //* 이미지 윤곽선 — 캡처 루트(자식)의 바깥이라 저장·공유 이미지엔 안 들어간다
        className="ring-1 ring-gray-300"
        style={{
          transform: `scale(${PREVIEW_DISPLAY_SCALE})`,
          transformOrigin: 'top center',
          //* transform은 레이아웃 공간을 줄이지 않으므로 줄어든 만큼 아래 여백을 회수
          marginBottom: -Math.round(captureHeight * (1 - PREVIEW_DISPLAY_SCALE)),
        }}
      >
        <div className="relative" ref={setCaptureRef}>
          <img src={localImageUrl} onLoad={() => setImageLoaded(true)} />
          <div
            ref={textRef}
            className={`noto-serif-kr pointer-events-none absolute z-10 w-full whitespace-pre-wrap break-keep font-medium`}
            style={{
              fontSize: `${fontSize}px`,
              lineHeight: `${lineHeight}px`,
              top: `${top}px`,
              paddingLeft: `${courtesyAsset?.textBox?.x0 ?? 24}px`,
              paddingRight: `${courtesyAsset?.textBox?.x1 ?? 24}px`,
            }}
          >
            <p>{courtesyContentString}</p>
          </div>
        </div>
      </div>
      {overflowed && (
        <p className="mt-2 text-12 font-medium leading-snug text-warn">
          글이 이미지 밖으로 넘쳤습니다. 저장·공유 시 잘리니 글씨를 줄이거나 위로
          올려주세요.
        </p>
      )}
      {/* 이미지를 보면서 글씨 크기·위치를 맞춘다 (캡처 영역 밖이라 결과 이미지엔 안 담김) */}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 p-1">
        <div className="flex items-center gap-1">
          <span className="shrink-0 text-12 font-bold text-gray-500">글씨</span>
          <button
            type="button"
            className={adjustButtonClassName}
            onClick={() =>
              setFontDelta((prev) => Math.max(prev - FONT_DELTA_STEP, FONT_DELTA_MIN))
            }
          >
            작게
          </button>
          <button
            type="button"
            className={adjustButtonClassName}
            onClick={() =>
              setFontDelta((prev) => Math.min(prev + FONT_DELTA_STEP, FONT_DELTA_MAX))
            }
          >
            크게
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span className="shrink-0 text-12 font-bold text-gray-500">위치</span>
          <button
            type="button"
            className={adjustButtonClassName}
            onClick={() =>
              setOffsetY((prev) => Math.max(prev - OFFSET_Y_STEP, OFFSET_Y_MIN))
            }
          >
            위
          </button>
          <button
            type="button"
            className={adjustButtonClassName}
            onClick={() =>
              setOffsetY((prev) => Math.min(prev + OFFSET_Y_STEP, OFFSET_Y_MAX))
            }
          >
            아래
          </button>
        </div>
        {isAdjusted && (
          <button
            type="button"
            onClick={() => {
              setFontDelta(0);
              setOffsetY(0);
            }}
            className={`${adjustButtonClassName} text-gray-500`}
          >
            초기화
          </button>
        )}
      </div>
      {/* 수정 · 공유 · 다운로드 한 줄 */}
      <div className="mt-2 grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={handleGoToEditClick}
          className={`${actionButtonClassName} border-0 bg-gray-800 text-white`}
        >
          <svg className="h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          <span>글 수정</span>
        </button>
        <Button
          onClick={handleShareClick}
          className={`${actionButtonClassName} bg-white text-black`}
        >
          <OtherApps className="wh-5 shrink-0" />
          <span>공유</span>
        </Button>
        <Button
          onClick={handleDownloadClick}
          className={`${actionButtonClassName} bg-white text-black`}
        >
          <Download className="wh-5 shrink-0 fill-gray-700" />
          <span>다운로드</span>
        </Button>
      </div>
    </Modal>
  );
};
